import {
  memo,
  useLayoutEffect,
  useRef,
  type ComponentPropsWithoutRef,
  type CompositionEvent,
  type FormEvent,
} from 'react';

// Imperatively replace a cell's HTML using DOM-child manipulation (the
// project rule blocks raw innerHTML *assignment*; appendChild + a parsed
// fragment is the supported workaround).
function setCellHtml(el: HTMLElement, html: string): void {
  while (el.firstChild) el.removeChild(el.firstChild);
  if (html === '') return;
  const range = document.createRange();
  range.selectNodeContents(el);
  el.appendChild(range.createContextualFragment(html));
}

type Props = Omit<
  ComponentPropsWithoutRef<'td'>,
  'children' | 'onInput'
> & {
  text: string;
  /**
   * Called with the live cell element on `input` / composition end. The
   * parent reads `dataset` + `innerHTML` — keep this path cheap (no sanitize).
   */
  onCellInput?: (cell: HTMLTableCellElement) => void;
  onCompositionChange?: (composing: boolean) => void;
};

/**
 * Uncontrolled contentEditable cell. Renders inline HTML imperatively
 * (allow-listed at write time elsewhere). While the cell is focused, the
 * DOM is the source of truth — props do not clobber the user's typing
 * and caret. When the cell is not focused (initial mount, defocus, or
 * external update), the DOM is synced from `text`.
 *
 * Selection formatting is owned by the parent table via
 * `SelectionToolbarProvider` (one host per table), not per cell.
 */
function EditableCellComponent({
  text,
  onCellInput,
  onCompositionChange,
  ...rest
}: Props) {
  const ref = useRef<HTMLTableCellElement>(null);
  const initializedRef = useRef(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Skip DOM sync if the user is currently editing this cell — typing
    // owns the DOM. Exception: initial mount, where we must establish
    // the starting content even if the cell happens to be focused.
    if (initializedRef.current && document.activeElement === el) return;
    setCellHtml(el, text);
    initializedRef.current = true;
  }, [text]);

  function handleInput(e: FormEvent<HTMLTableCellElement>): void {
    const native = e.nativeEvent as InputEvent;
    if (native.isComposing) return;
    onCellInput?.(e.currentTarget);
  }

  function handleCompositionStart(): void {
    onCompositionChange?.(true);
  }

  function handleCompositionEnd(
    e: CompositionEvent<HTMLTableCellElement>,
  ): void {
    onCompositionChange?.(false);
    onCellInput?.(e.currentTarget);
  }

  return (
    <td
      ref={ref}
      onInput={handleInput}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      {...rest}
    />
  );
}

export const EditableCell = memo(EditableCellComponent);
